import ChessNav from './ChessNav';
import React, { useState, useEffect } from 'react';
import { db } from '../../config/firebase';
import {
  collection, addDoc, query, where, onSnapshot,
  serverTimestamp, doc, updateDoc, getDocs, limit,
  setDoc, orderBy, runTransaction
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { v4 as uuidv4 } from 'uuid';
import { Chess } from 'chess.js';
import { DEFAULT_RATING, getTier } from '../../utils/chessRating';
import { makeSalt, hashPassword, cryptoAvailable, validateName, validatePassword } from '../../utils/chessAuth';
import { getBotDifficulty, getBotVirtualRating } from '../../utils/chessBot';
import Icon from '../Shared/Icons';
import { C } from '../../utils/chessTheme';
import { FONT } from '../../utils/chessTheme';
import logo from '../../logo.png';

const PLAYER_KEY = 'iyanna_chess_player';

function loadPlayer() {
  try {
    const raw = localStorage.getItem(PLAYER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function savePlayer(player) {
  localStorage.setItem(PLAYER_KEY, JSON.stringify(player));
}

function genInviteCode() {
  return Math.random().toString(36).slice(2, 8).toUpperCase();
}

const TIME_CONTROLS = [
  { minutes: 1, label: '1 min', tag: 'Bullet' },
  { minutes: 3, label: '3 min', tag: 'Blitz' },
  { minutes: 5, label: '5 min', tag: 'Blitz' },
  { minutes: 10, label: '10 min', tag: 'Rapid' },
  { minutes: 15, label: '15 min', tag: 'Rapid' },
  { minutes: 30, label: '30 min', tag: 'Classical' },
];
const TIME_CONTROL_KEY = 'iyanna_chess_time_control';

// Temporarily disabled: linking a play session to a real registered
// student's name/school surfaces third-party data that was entered by
// Staff, not the child themselves — same consent concern as the rest of
// the site. Flip this back to true once a proper consent process exists.
const LINK_STUDENT_ENABLED = false;

export default function ChessLobby() {
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const [player, setPlayer] = useState(loadPlayer());
  const [nameInput, setNameInput] = useState('');
  const [playSheetOpen, setPlaySheetOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login' | 'signup'
  const [showForgot, setShowForgot] = useState(false);
  const [passInput, setPassInput] = useState('');
  const [pass2Input, setPass2Input] = useState('');
  const [authBusy, setAuthBusy] = useState(false);
  const [linkStudent, setLinkStudent] = useState(false);
  const [students, setStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState('');

  const [liveGames, setLiveGames] = useState([]);
  const [topPlayers, setTopPlayers] = useState([]);
  const [weekChampion, setWeekChampion] = useState(null);
  const [waitingGames, setWaitingGames] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [selectedMinutes, setSelectedMinutes] = useState(() => {
    const saved = Number(localStorage.getItem(TIME_CONTROL_KEY));
    return TIME_CONTROLS.some(t => t.minutes === saved) ? saved : 10;
  });

  function handleSelectTime(minutes) {
    setSelectedMinutes(minutes);
    localStorage.setItem(TIME_CONTROL_KEY, String(minutes));
  }

  const [joinCode, setJoinCode] = useState('');
  const [showJoinBox, setShowJoinBox] = useState(false);

  // Which color the player wants against the BOT — full freedom to choose.
  const BOT_COLOR_KEY = 'iyanna_chess_bot_color';
  const [botColorChoice, setBotColorChoice] = useState(() => {
    try {
      const v = localStorage.getItem(BOT_COLOR_KEY);
      return ['white', 'black', 'random'].includes(v) ? v : 'white';
    } catch { return 'white'; }
  });
  function handleSelectBotColor(c) {
    setBotColorChoice(c);
    try { localStorage.setItem(BOT_COLOR_KEY, c); } catch {}
  }

  // Human-vs-human games ALTERNATE colors: if your last game was white,
  // the next one gives you black — back and forth for as long as you play.
  const LAST_COLOR_KEY = 'iyanna_chess_last_color';
  function nextAlternatingColor() {
    try {
      const last = localStorage.getItem(LAST_COLOR_KEY);
      if (last === 'white') return 'black';
      if (last === 'black') return 'white';
    } catch {}
    return 'white';
  }
  function rememberColor(c) {
    try { localStorage.setItem(LAST_COLOR_KEY, c); } catch {}
  }

  // Registered chess students, for linking rating
  useEffect(() => {
    if (!linkStudent) return;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'chessStudents'));
        setStudents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) { console.error(e); }
    })();
  }, [linkStudent]);

  // Keep my displayed rating in sync with the authoritative leaderboard
  // entry (queried by nameLower, same record the ranking shows). Repairs
  // the cached localStorage value after any admin ratings change.
  useEffect(() => {
    if (!player?.name) return;
    const nameLower = player.name.toLowerCase();
    const qy = query(collection(db, 'chessPlayers'), where('nameLower', '==', nameLower), limit(1));
    const unsub = onSnapshot(qy, snap => {
      if (snap.empty) return;
      const r = snap.docs[0].data().rating;
      if (typeof r === 'number' && r !== player.rating) {
        setPlayer(prev => {
          if (!prev) return prev;
          const next = { ...prev, rating: r };
          try { localStorage.setItem(PLAYER_KEY, JSON.stringify(next)); } catch {}
          return next;
        });
      }
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player?.name]);

  // Live (active) public games — spectate list. Single-field query (no
  // composite index needed); privacy filtered on the client.
  useEffect(() => {
    const q = query(collection(db, 'chessGames'), where('status', '==', 'active'));
    const unsub = onSnapshot(q, snap => {
      setLiveGames(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(g => !g.isPrivate));
    });
    return unsub;
  }, []);

  // Waiting public games — open to join. Same index-free pattern.
  useEffect(() => {
    const q = query(collection(db, 'chessGames'), where('status', '==', 'waiting'));
    const unsub = onSnapshot(q, snap => {
      setWaitingGames(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(g => !g.isPrivate));
    });
    return unsub;
  }, []);

  // Top players leaderboard — inspires everyone to climb the ladder.
  // Only self-chosen nicknames live here, so it's safe to show publicly.
  useEffect(() => {
    const q = query(
      collection(db, 'chessPlayers'),
      orderBy('rating', 'desc'),
      limit(10)
    );
    const unsub = onSnapshot(q, snap => {
      setTopPlayers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, e => console.error(e));
    return unsub;
  }, []);

  // Bingwa wa Wiki — the biggest rating climber of THIS week, live.
  useEffect(() => {
    const monday = new Date();
    monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
    const week = monday.toISOString().slice(0, 10);
    const q = query(
      collection(db, 'chessPlayers'),
      orderBy('weekDelta', 'desc'),
      limit(10)
    );
    const unsub = onSnapshot(q, snap => {
      const climbers = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(p => p.weekKey === week && (p.weekDelta || 0) > 0);
      setWeekChampion(climbers[0] || null);
    }, e => console.error(e));
    return unsub;
  }, []);

  /** Look up a public chess profile by nickname (case-insensitive). */
  async function findProfile(nameLower) {
    const snap = await getDocs(query(
      collection(db, 'chessPlayers'),
      where('nameLower', '==', nameLower),
      limit(1)
    ));
    if (snap.empty) return null;
    const d = snap.docs[0];
    return { id: d.id, ...d.data() };
  }

  async function handleSignup(e) {
    e.preventDefault();
    if (authBusy) return;
    setError('');

    const wanted = nameInput.trim();
    const nameErr = validateName(wanted);
    if (nameErr) { setError(nameErr); return; }
    const passErr = validatePassword(passInput);
    if (passErr) { setError(passErr); return; }
    if (passInput !== pass2Input) { setError('Password mbili hazifanani.'); return; }
    if (!cryptoAvailable()) {
      setError('Kivinjari hiki hakiruhusu usalama wa password. Tumia https au kivinjari kingine.');
      return;
    }

    setAuthBusy(true);
    const nameLower = wanted.toLowerCase();
    const salt = makeSalt();
    let passHash;
    try {
      passHash = await hashPassword(passInput, salt);
    } catch (err) {
      console.error(err);
      setAuthBusy(false);
      setError('Imeshindikana kutengeneza usalama wa password. Jaribu tena.');
      return;
    }

    // The rules only allow CREATE when this nickname is still free, so this
    // single write is what guarantees no two players share a name.
    try {
      await setDoc(doc(db, 'chessAuth', nameLower), {
        passHash,
        createdAt: serverTimestamp(),
      });
    } catch (err) {
      console.error(err);
      setAuthBusy(false);
      setError('Jina "' + wanted + '" tayari limechukuliwa. Chagua jina lingine, au bofya "Ingia" kama ni lako.');
      return;
    }

    try {
      // If a profile with this nickname already existed (from before
      // passwords), keep it — the player keeps their rating and history.
      const existing = await findProfile(nameLower);
      const newPlayer = existing
        ? { id: existing.id, name: existing.name, rating: existing.rating || DEFAULT_RATING, studentId: null }
        : { id: uuidv4(), name: wanted, rating: DEFAULT_RATING, studentId: null };

      await setDoc(doc(db, 'chessPlayers', newPlayer.id), {
        name: newPlayer.name,
        nameLower,
        salt,
        rating: newPlayer.rating,
        updatedAt: serverTimestamp(),
      }, { merge: true });

      savePlayer(newPlayer);
      setPlayer(newPlayer);
      setPassInput('');
      setPass2Input('');
    } catch (err) {
      console.error(err);
      setError('Akaunti imefunguliwa lakini profile haijahifadhiwa. Jaribu kuingia tena.');
    }
    setAuthBusy(false);
  }

  async function handleLogin(e) {
    e.preventDefault();
    if (authBusy) return;
    setError('');

    const wanted = nameInput.trim();
    if (!wanted || !passInput) return;
    if (!cryptoAvailable()) {
      setError('Kivinjari hiki hakiruhusu usalama wa password. Tumia https au kivinjari kingine.');
      return;
    }

    setAuthBusy(true);
    const nameLower = wanted.toLowerCase();
    try {
      const profile = await findProfile(nameLower);
      if (!profile) {
        setAuthBusy(false);
        setError('Jina hili halijasajiliwa. Bofya "Fungua Akaunti" kuanza.');
        return;
      }
      if (!profile.salt) {
        setAuthBusy(false);
        setError('Akaunti hii bado haina password. Bofya "Fungua Akaunti" ukitumia jina hili ili kuiweka — rating yako itabaki.');
        return;
      }

      const passHash = await hashPassword(passInput, profile.salt);
      // Rules accept this write only when the hash matches what is stored,
      // so a successful write IS the proof of a correct password.
      await updateDoc(doc(db, 'chessAuth', nameLower), {
        passHash,
        lastLogin: serverTimestamp(),
      });

      const loggedIn = {
        id: profile.id,
        name: profile.name,
        rating: profile.rating || DEFAULT_RATING,
        studentId: null,
      };
      savePlayer(loggedIn);
      setPlayer(loggedIn);
      setPassInput('');
    } catch (err) {
      console.error(err);
      setError('Password si sahihi. Jaribu tena.');
    }
    setAuthBusy(false);
  }

  function handleChangeName() {
    localStorage.removeItem(PLAYER_KEY);
    setPlayer(null);
    setNameInput('');
    setPassInput('');
    setPass2Input('');
    setAuthMode('login');
    setError('');
    setLinkStudent(false);
    setSelectedStudentId('');
  }

  async function handlePlayBot() {
    if (!player) return;
    setBusy(true);
    setError('');
    try {
      const diff = getBotDifficulty(player.rating);
      const botRating = getBotVirtualRating(player.rating);
      const startFen = new Chess().fen();
      const clockSeconds = selectedMinutes * 60;
      // The player picked their color freely (white / black / random).
      const humanIsWhite = botColorChoice === 'random'
        ? Math.random() < 0.5
        : botColorChoice === 'white';
      const humanSeat = { id: player.id, name: player.name, rating: player.rating, studentId: player.studentId || null };
      const botSeat = { id: 'bot', name: `${diff.label}`, rating: botRating, isBot: true };
      const ref = await addDoc(collection(db, 'chessGames'), {
        isPrivate: true,
        isBotGame: true,
        botLabel: diff.label,
        inviteCode: null,
        status: 'active',
        fen: startFen,
        turn: 'w',
        white: humanIsWhite ? humanSeat : botSeat,
        black: humanIsWhite ? botSeat : humanSeat,
        winner: null,
        resultReason: null,
        clockSeconds,
        whiteTimeLeft: clockSeconds,
        blackTimeLeft: clockSeconds,
        turnStartedAt: serverTimestamp(),
        lastFrom: null,
        lastTo: null,
        moveHistory: [],
        createdAt: serverTimestamp(),
      });
      navigate(`/chess/room/${ref.id}`);
    } catch (e) {
      console.error(e);
      setError('Imeshindikana kuanzisha mchezo na Bot.');
    }
    setBusy(false);
  }

  async function handlePlayRandom() {
    if (!player) return;
    setBusy(true);
    setError('');
    try {
      // Try to find an open public game waiting for an opponent
      const q = query(
        collection(db, 'chessGames'),
        where('isPrivate', '==', false),
        where('status', '==', 'waiting')
      );
      const snap = await getDocs(q);
      const openGame = snap.docs.find(d => {
        const g = d.data();
        return g.white?.id !== player.id && g.black?.id !== player.id;
      });

      if (openGame) {
        await joinOpenSeat(openGame.id, openGame.data());
        navigate(`/chess/room/${openGame.id}`);
      } else {
        const gameId = await createGame({ isPrivate: false });
        navigate(`/chess/room/${gameId}`);
      }
    } catch (e) {
      console.error(e);
      setError('Imeshindikana kuanzisha mchezo. Jaribu tena.');
    }
    setBusy(false);
  }

  async function handleCreatePrivate() {
    if (!player) return;
    setBusy(true);
    setError('');
    try {
      const gameId = await createGame({ isPrivate: true });
      navigate(`/chess/room/${gameId}`);
    } catch (e) {
      console.error(e);
      setError('Imeshindikana kuanzisha mchezo binafsi.');
    }
    setBusy(false);
  }

  async function handleCreateClub() {
    // A "club" game is an in-person meeting on one device: two people take
    // turns on the same board. It carries an activity bonus to reward
    // gathering face to face. It is private (code-shareable) but flagged.
    if (!player) return;
    setBusy(true);
    setError('');
    try {
      const gameId = await createGame({ isPrivate: true, isClub: true });
      navigate(`/chess/room/${gameId}`);
    } catch (e) {
      console.error(e);
      setError('Imeshindikana kuanzisha mchezo wa klabu.');
    }
    setBusy(false);
  }

  async function handleJoinPrivate(e) {
    e.preventDefault();
    if (!player || !joinCode.trim()) return;
    setBusy(true);
    setError('');
    try {
      const q = query(
        collection(db, 'chessGames'),
        where('inviteCode', '==', joinCode.trim().toUpperCase()),
        limit(1)
      );
      const snap = await getDocs(q);
      if (snap.empty) {
        setError('Msimbo huo haupo. Angalia tena.');
        setBusy(false);
        return;
      }
      const gameDoc = snap.docs[0];
      const data = gameDoc.data();
      if (data.status === 'waiting' && data.white?.id !== player.id && data.black?.id !== player.id) {
        await joinOpenSeat(gameDoc.id, data);
      }
      navigate(`/chess/room/${gameDoc.id}`);
    } catch (e) {
      console.error(e);
      setError('Hitilafu imetokea. Jaribu tena.');
    }
    setBusy(false);
  }

  async function createGame({ isPrivate, isClub = false }) {
    const startFen = new Chess().fen();
    const clockSeconds = selectedMinutes * 60;
    // Alternate: whoever played white last time creates as black now.
    const myColor = nextAlternatingColor();
    rememberColor(myColor);
    const mySeat = { id: player.id, name: player.name, rating: player.rating, studentId: player.studentId || null };
    const ref = await addDoc(collection(db, 'chessGames'), {
      isPrivate,
      isClub,
      inviteCode: isPrivate ? genInviteCode() : null,
      status: 'waiting',
      fen: startFen,
      turn: 'w',
      white: myColor === 'white' ? mySeat : null,
      black: myColor === 'black' ? mySeat : null,
      winner: null,
      resultReason: null,
      clockSeconds,
      whiteTimeLeft: clockSeconds,
      blackTimeLeft: clockSeconds,
      turnStartedAt: null,
      lastFrom: null,
      lastTo: null,
      moveHistory: [],
      createdAt: serverTimestamp(),
    });
    return ref.id;
  }

  async function joinOpenSeat(gameId, gameData) {
    // Fill whichever seat is empty. Done inside a transaction reading the
    // FRESH doc, so two people can't grab the same seat and a stale seat
    // snapshot can't overwrite the creator — the cause of "my friend
    // joined but couldn't move on their turn".
    const mySeat = { id: player.id, name: player.name, rating: player.rating, studentId: player.studentId || null };
    try {
      const seat = await runTransaction(db, async (tx) => {
        const ref = doc(db, 'chessGames', gameId);
        const snap = await tx.get(ref);
        if (!snap.exists()) throw new Error('missing');
        const g = snap.data();
        // Already seated here? Nothing to do.
        if (g.white?.id === player.id || g.black?.id === player.id) return null;
        const openSeat = g.white == null ? 'white' : g.black == null ? 'black' : null;
        if (!openSeat) throw new Error('full');
        tx.update(ref, {
          [openSeat]: mySeat,
          status: 'active',
          turnStartedAt: serverTimestamp(),
        });
        return openSeat;
      });
      if (seat) rememberColor(seat);
    } catch (e) {
      console.error('join seat', e);
      // Fall back to the simple write so a transient transaction error
      // never leaves the friend unseated.
      const openSeat = gameData?.white == null ? 'white' : 'black';
      rememberColor(openSeat);
      await updateDoc(doc(db, 'chessGames', gameId), {
        [openSeat]: mySeat, status: 'active', turnStartedAt: serverTimestamp(),
      });
    }
  }

  const tier = player ? getTier(player.rating) : null;

  // ── Signup / Login screen ──
  if (!player) {
    return (
      <div style={styles.welcomePage}>
        <div style={styles.welcomeInner}>
          <div style={styles.welcomeLogo}>
            <img src={logo} alt="Iyanna Foundation" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          </div>
          <div style={styles.welcomeEyebrow}>IYANNA FOUNDATION</div>
          <div style={styles.welcomeTitle}>Iyanna Chess</div>
          <div style={styles.welcomeSub}>
            Cheza. Jifunze. Kua. — Chess inayokujenga ndani na nje ya ubao.
          </div>

          <div style={styles.welcomeFeatures}>
            {[
              { icon: 'bot', label: 'Bot Coach' },
              { icon: 'trophy', label: 'Mashindano' },
              { icon: 'sparkle', label: 'Somo la Maisha' },
            ].map((ft, i) => (
              <div key={i} style={styles.welcomeFeature}>
                <Icon name={ft.icon} size={18} color="#34D399" />
                <span style={styles.welcomeFeatureLabel}>{ft.label}</span>
              </div>
            ))}
          </div>

          <div style={styles.setupCard}>
            {/* MODE SWITCH */}
            <div style={styles.authTabs}>
              <button
                style={{ ...styles.authTab, ...(authMode === 'login' ? styles.authTabActive : {}) }}
                onClick={() => { setAuthMode('login'); setError(''); }}
              >
                Ingia
              </button>
              <button
                style={{ ...styles.authTab, ...(authMode === 'signup' ? styles.authTabActive : {}) }}
                onClick={() => { setAuthMode('signup'); setError(''); }}
              >
                Fungua Akaunti
              </button>
            </div>

            <div style={styles.setupTitle}>
              {authMode === 'login' ? 'Karibu tena!' : 'Anza safari yako'}
            </div>

            {error && <div style={styles.errorMsg}>{error}</div>}

            <form onSubmit={authMode === 'login' ? handleLogin : handleSignup}>
              <input
                style={styles.input}
                value={nameInput}
                onChange={e => setNameInput(e.target.value)}
                placeholder="Jina lako la mchezo"
                autoComplete="username"
                required
              />
              <input
                style={styles.input}
                type="password"
                value={passInput}
                onChange={e => setPassInput(e.target.value)}
                placeholder="Password"
                autoComplete={authMode === 'login' ? 'current-password' : 'new-password'}
                required
              />
              {authMode === 'signup' && (
                <input
                  style={styles.input}
                  type="password"
                  value={pass2Input}
                  onChange={e => setPass2Input(e.target.value)}
                  placeholder="Rudia password"
                  autoComplete="new-password"
                  required
                />
              )}
              <button type="submit" style={styles.primaryBtn} disabled={authBusy}>
                {authBusy
                  ? 'Subiri kidogo…'
                  : authMode === 'login' ? 'Ingia →' : 'Fungua Akaunti →'}
              </button>
            </form>

            {authMode === 'login' && (
              <button type="button" style={styles.forgotLink} onClick={() => setShowForgot(true)}>
                Umesahau password?
              </button>
            )}

            <div style={styles.authHint}>
              {authMode === 'login'
                ? 'Ulishafungua akaunti kabla ya password kuanzishwa? Chagua "Fungua Akaunti", tumia jina lako lile lile — utaunganishwa na rating yako.'
                : 'Jina lako litakuwa lako peke yako. Hakuna mwingine atakayeweza kulitumia.'}
            </div>
          </div>

          {showForgot && (
            <div style={styles.forgotOverlay} onClick={() => setShowForgot(false)}>
              <div style={styles.forgotCard} onClick={e => e.stopPropagation()}>
                <div style={styles.forgotTitle}>Umesahau Password?</div>
                <div style={styles.forgotText}>
                  Kwa usalama, password yako haiwezi kusomwa na mtu yeyote — hata sisi.
                  Wasiliana na walimu wa Iyanna Foundation ili wakusaidie kufungua
                  akaunti yako upya. Rating yako, coins na historia yako yote itabaki
                  salama kama ilivyo.
                </div>
                <button style={styles.forgotClose} onClick={() => setShowForgot(false)}>Nimeelewa</button>
              </div>
            </div>
          )}

          <div style={styles.welcomeFoot}>
            Bure kabisa · Hakuna matangazo · Jina la utani tu
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {/* Minimal top bar — no heavy container */}
      <div style={styles.bar}>
        <div style={styles.barBrand}>
          <div style={styles.barLogo}>
            <img src={logo} alt="Iyanna" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          </div>
          <span style={styles.barName}>Iyanna Chess</span>
        </div>
        <div style={styles.barRight}>
          <div style={styles.ratingPill}>
            <span style={{ ...styles.pillDot, background: tier.color }} />
            <span style={styles.pillRating}>{player.rating}</span>
          </div>
          <button style={styles.homeBackBtn} onClick={() => navigate('/')} title="Rudi mwanzo">
            <Icon name="door" size={19} color={C.text} strokeWidth={2.4} />
          </button>
        </div>
      </div>

      {error && <div style={styles.errorMsg}>{error}</div>}

      {/* Calm greeting — premium, minimal, no boxed hero */}
      <div style={styles.hero}>
        <div style={styles.heroHi}>Habari, {player.name.split(' ')[0]}</div>
        <div style={styles.heroTierRow}>
          <span style={{ ...styles.heroTier, color: tier.color }}>{tier.swahili}</span>
          <span style={styles.heroDot}>·</span>
          <span style={styles.heroRating}>{player.rating} rating</span>
        </div>
      </div>

      {/* Quick actions — clean icon row, no heavy cards or captions */}
      <div style={styles.actions}>
        {[
          { icon: 'trophy', label: 'Mashindano', path: '/chess/tournaments' },
          { icon: 'puzzle', label: 'Puzzles', path: '/chess/puzzle' },
          { icon: 'book', label: 'Jifunze', path: '/chess/learn' },
          { icon: 'crown', label: 'Mabingwa', path: '/chess/hall-of-fame' },
          { icon: 'verified', label: 'IFR Rasmi', path: '/rating' },
        ].map((q, i) => (
          <button key={i} style={styles.action} onClick={() => navigate(q.path)}>
            <span style={styles.actionIcon}><Icon name={q.icon} size={20} color={C.accent} strokeWidth={2} /></span>
            <span style={styles.actionLabel}>{q.label}</span>
          </button>
        ))}
      </div>

      {(userRole === 'admin' || userRole === 'staff') && (
        <button style={styles.adminBar} onClick={() => navigate('/chess/admin')}>
          <Icon name="wrench" size={15} color={C.accent} strokeWidth={2} />
          <span>Chess Admin — simamia wachezaji & shughuli</span>
          <span style={styles.adminChev}>›</span>
        </button>
      )}

      {/* PLAY SHEET — pick time + opponent in one calm place */}
      {playSheetOpen && (
        <div style={styles.sheetOverlay} onClick={() => setPlaySheetOpen(false)}>
          <div style={styles.sheet} onClick={e => e.stopPropagation()}>
            <div style={styles.sheetHandle} />
            <div style={styles.sheetTitle}>Chagua Mchezo</div>

            <div style={styles.sheetLabel}>Muda wa mchezo</div>
            <div style={styles.timeRow}>
              {TIME_CONTROLS.map(t => (
                <button
                  key={t.minutes}
                  style={{ ...styles.timeChip, ...(selectedMinutes === t.minutes ? styles.timeChipActive : {}) }}
                  onClick={() => handleSelectTime(t.minutes)}
                >
                  <span style={styles.timeChipMin}>{t.label}</span>
                  <span style={styles.timeChipTag}>{t.tag}</span>
                </button>
              ))}
            </div>

            <div style={styles.sheetLabel}>Rangi yako (ukicheza na Bot)</div>
            <div style={styles.colorRow}>
              {[
                { key: 'white', label: 'Nyeupe', glyph: '♔' },
                { key: 'black', label: 'Nyeusi', glyph: '♚' },
                { key: 'random', label: 'Bahati', glyph: '⚄' },
              ].map(c => (
                <button
                  key={c.key}
                  style={{ ...styles.colorChip, ...(botColorChoice === c.key ? styles.colorChipActive : {}) }}
                  onClick={() => handleSelectBotColor(c.key)}
                >
                  <span style={styles.colorGlyph}>{c.glyph}</span>
                  <span>{c.label}</span>
                </button>
              ))}
            </div>

            <div style={styles.sheetLabel}>Unacheza na nani?</div>
            <div style={styles.modeList}>
              <button style={styles.modeCard} disabled={busy} onClick={() => { setPlaySheetOpen(false); handlePlayBot(); }}>
                <span style={{ ...styles.modeIcon, background: 'rgba(255,167,38,0.15)' }}>
                  <Icon name="bot" size={22} color="#34D399" />
                </span>
                <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                  <span style={styles.modeTitle}>Cheza na Bot</span>
                  <span style={styles.modeSub}>
                    Anaanza mara moja · unacheza {botColorChoice === 'white' ? 'Nyeupe' : botColorChoice === 'black' ? 'Nyeusi' : 'rangi ya bahati'}
                  </span>
                </span>
                <span style={styles.modeGo}>›</span>
              </button>

              <button style={styles.modeCard} disabled={busy} onClick={() => { setPlaySheetOpen(false); handlePlayRandom(); }}>
                <span style={{ ...styles.modeIcon, background: 'rgba(52,211,153,0.15)' }}>
                  <Icon name="bolt" size={22} color="#34D399" />
                </span>
                <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                  <span style={styles.modeTitle}>Mpinzani wa Bahati</span>
                  <span style={styles.modeSub}>Mchezo wa wazi · yeyote anaweza kujiunga</span>
                </span>
                <span style={styles.modeGo}>›</span>
              </button>

              <button style={styles.modeCard} disabled={busy} onClick={() => { setPlaySheetOpen(false); handleCreatePrivate(); }}>
                <span style={{ ...styles.modeIcon, background: 'rgba(95,208,138,0.15)' }}>
                  <Icon name="lock" size={22} color="#5FD08A" />
                </span>
                <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                  <span style={styles.modeTitle}>Alika Rafiki</span>
                  <span style={styles.modeSub}>Unapata CODE ya siri ya kumtumia</span>
                </span>
                <span style={styles.modeGo}>›</span>
              </button>

              <button style={{ ...styles.modeCard, ...styles.modeCardClub }} disabled={busy} onClick={() => { setPlaySheetOpen(false); handleCreateClub(); }}>
                <span style={{ ...styles.modeIcon, background: 'rgba(52,211,153,0.18)' }}>
                  <Icon name="parents" size={22} color="#34D399" />
                </span>
                <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                  <span style={styles.modeTitle}>
                    Chess Club <span style={styles.bonusPill}>+bonus</span>
                  </span>
                  <span style={styles.modeSub}>Mkutano wa ana kwa ana · pointi za ziada za kuhudhuria</span>
                </span>
                <span style={styles.modeGo}>›</span>
              </button>

              <button style={styles.modeCard} onClick={() => setShowJoinBox(!showJoinBox)}>
                <span style={{ ...styles.modeIcon, background: 'rgba(129,212,250,0.15)' }}>
                  <Icon name="key" size={22} color="#7FD4FA" />
                </span>
                <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                  <span style={styles.modeTitle}>Ingia kwa CODE</span>
                  <span style={styles.modeSub}>Umepewa code na rafiki? Iandike hapa</span>
                </span>
                <span style={styles.modeGo}>{showJoinBox ? '⌄' : '›'}</span>
              </button>

              {showJoinBox && (
                <form onSubmit={handleJoinPrivate} style={styles.joinBox}>
                  <input
                    style={styles.input}
                    value={joinCode}
                    onChange={e => setJoinCode(e.target.value)}
                    placeholder="mfano: A1B2C3"
                  />
                  <button type="submit" style={styles.primaryBtnSm} disabled={busy}>Ingia →</button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* BINGWA WA WIKI — this week's top climber, live */}
      {weekChampion && (
        <div style={styles.championBanner}>
          <div style={styles.championIcon}><Icon name="trophy" size={24} color="#4ADE80" /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={styles.championLabel}>Bingwa wa Wiki Hii</div>
            <div style={styles.championName}>{weekChampion.name}</div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={styles.championDelta}>▲ {weekChampion.weekDelta} wiki hii</div>
            <div style={styles.championLink} onClick={() => navigate('/chess/hall-of-fame')}>Hall of Fame →</div>
          </div>
        </div>
      )}

      {/* OPEN GAMES — join with one tap */}
      <div style={styles.openHead}>
        <span style={styles.openTitle}>Michezo Iliyo Wazi</span>
        <button style={styles.liveLink} onClick={() => navigate('/chess/watch')}>
          <Icon name="play" size={13} color={C.accent} strokeWidth={2.5} /> Live
          {liveGames.length > 0 && <span style={styles.liveCount}>{liveGames.length}</span>}
        </button>
      </div>
      {waitingGames.length === 0 ? (
        <div style={styles.empty}>Hakuna mchezo wazi sasa — bonyeza Play kuanzisha.</div>
      ) : (
        <div style={styles.gameList}>
          {waitingGames.map(g => {
            const creator = g.white || g.black;
            const iAmIn = g.white?.id === player.id || g.black?.id === player.id;
            const openSeatLabel = g.white == null ? 'Nyeupe' : 'Nyeusi';
            return (
              <div key={g.id} style={styles.gameRow} onClick={async () => {
                if (iAmIn) { navigate(`/chess/room/${g.id}`); return; }
                await joinOpenSeat(g.id, g);
                navigate(`/chess/room/${g.id}`);
              }}>
                <span style={styles.gameAvatar}>{(creator?.name || 'P')[0].toUpperCase()}</span>
                <span style={styles.gamePlayer}>{creator?.name || 'Mchezaji'} · {creator?.rating || DEFAULT_RATING}</span>
                <span style={styles.joinTag}>{iAmIn ? 'Ingia →' : `Jiunge (${openSeatLabel}) →`}</span>
              </div>
            );
          })}
        </div>
      )}
      {/* Fixed Play button — bottom of screen for easy thumb reach */}
      <button
        style={{ ...styles.bottomPlay, ...(busy ? { opacity: 0.6 } : {}) }}
        onClick={() => setPlaySheetOpen(true)}
        disabled={busy}
      >
        <Icon name="play" size={20} color="#06281A" strokeWidth={2.5} />
        <span>Play</span>
      </button>

      <ChessNav />
    </div>
  );
}

const styles = {
  homeBackBtn: { width: '40px', height: '40px', borderRadius: '12px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  bottomPlay: { position: 'fixed', left: '50%', transform: 'translateX(-50%)', bottom: '98px', zIndex: 50, display: 'inline-flex', alignItems: 'center', gap: '9px', background: 'linear-gradient(135deg, #6EE7A8, #34D399)', color: '#06281A', border: 'none', padding: '15px 40px', borderRadius: '99px', fontSize: '16px', fontWeight: '800', cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 12px 30px rgba(52,211,153,0.4)' },
  forgotLink: { display: 'block', margin: '10px auto 0', background: 'transparent', border: 'none', color: '#34D399', fontSize: '12.5px', fontWeight: '800', cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline' },
  forgotOverlay: { position: 'fixed', inset: 0, zIndex: 980, background: 'rgba(10,13,12,0.8)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '22px' },
  forgotCard: { width: '100%', maxWidth: '340px', background: 'linear-gradient(180deg, #2A302D, #1E2421)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '18px', padding: '22px', textAlign: 'center' },
  forgotTitle: { color: '#ECEFEA', fontFamily: "'Fraunces', Georgia, serif", fontSize: '18px', fontWeight: '800', marginBottom: '10px' },
  forgotText: { color: 'rgba(236,239,234,0.65)', fontSize: '13px', lineHeight: 1.7, marginBottom: '16px' },
  forgotClose: { background: 'linear-gradient(135deg, #0E9F6E, #34D399)', color: '#06281A', border: 'none', padding: '11px 24px', borderRadius: '11px', fontSize: '13px', fontWeight: '800', cursor: 'pointer', fontFamily: 'inherit' },
  liveFeedBtn: {
    maxWidth: '900px', margin: '18px auto 0', width: '100%',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '9px',
    background: 'linear-gradient(135deg, #6EE7A8, #34D399)', color: '#06281A',
    border: 'none', padding: '14px 20px', borderRadius: '14px',
    fontSize: '14px', fontWeight: '800', cursor: 'pointer', fontFamily: 'inherit',
    boxShadow: '0 10px 26px rgba(52,211,153,0.25)',
  },
  liveCount: {
    background: '#06281A', color: '#6EE7A8', fontSize: '11px', fontWeight: '800',
    padding: '2px 9px', borderRadius: '99px',
  },
  page: {
    minHeight: '100vh',
    background: 'radial-gradient(circle at 30% 0%, #262D2A 0%, #1B201E 48%, #121614 100%)',
    padding: '20px 18px 60px', paddingBottom: '150px', maxWidth: '620px', margin: '0 auto',
  },
  bar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '26px' },
  barBrand: { display: 'flex', alignItems: 'center', gap: '10px' },
  barLogo: { width: '34px', height: '34px', borderRadius: '50%', overflow: 'hidden', border: '1.5px solid rgba(255,255,255,0.14)' },
  barName: { color: C.text, fontFamily: FONT.display, fontSize: '17px', fontWeight: '800', letterSpacing: '0.2px' },
  barRight: { display: 'flex', alignItems: 'center', gap: '9px' },
  ratingPill: { display: 'inline-flex', alignItems: 'center', gap: '7px', background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.line}`, borderRadius: '99px', padding: '7px 13px' },
  pillDot: { width: '8px', height: '8px', borderRadius: '50%' },
  pillRating: { color: C.text, fontSize: '13px', fontWeight: '800' },
  homeBackBtn: { width: '38px', height: '38px', borderRadius: '11px', background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.line}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },

  hero: { marginBottom: '24px' },
  heroHi: { color: C.text, fontFamily: FONT.display, fontSize: '30px', fontWeight: '800', letterSpacing: '-0.5px' },
  heroTierRow: { display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' },
  heroTier: { fontSize: '13.5px', fontWeight: '800' },
  heroDot: { color: C.textFaint },
  heroRating: { color: C.textDim, fontSize: '13.5px', fontWeight: '700' },

  adminBar: { display: 'flex', alignItems: 'center', gap: '9px', width: '100%', background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.28)', color: '#D9F5E7', fontSize: '12.5px', fontWeight: '700', padding: '12px 15px', borderRadius: '13px', cursor: 'pointer', fontFamily: 'inherit', marginBottom: '22px' },
  adminChev: { marginLeft: 'auto', color: 'rgba(236,239,234,0.5)', fontSize: '18px' },
  actions: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '26px' },
  action: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: '4px 0' },
  actionIcon: { width: '54px', height: '54px', borderRadius: '17px', background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  actionLabel: { color: C.textDim, fontSize: '11px', fontWeight: '700' },

  openHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' },
  openTitle: { color: C.text, fontSize: '14px', fontWeight: '800' },
  liveLink: { display: 'inline-flex', alignItems: 'center', gap: '5px', background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.3)', color: C.accent, fontSize: '12px', fontWeight: '800', padding: '6px 12px', borderRadius: '99px', cursor: 'pointer', fontFamily: 'inherit' },
  gameAvatar: { width: '34px', height: '34px', borderRadius: '50%', background: 'rgba(52,211,153,0.12)', color: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '800', flexShrink: 0 },
  _oldheader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '16px',
    maxWidth: '900px',
    margin: '0 auto 24px',
  },
  brandRow: { display: 'flex', alignItems: 'center', gap: '12px' },
  brandLogo: { width: '48px', height: '48px', borderRadius: '50%', background: '#121614', padding: '0px', border: '2px solid rgba(255,255,255,0.15)', overflow: 'hidden'},
  brandName: { fontFamily: "'Fraunces', Georgia, serif", fontSize: '20px', color: '#fff', fontWeight: '700' },
  brandSub: { fontSize: '10.5px', color: 'rgba(255,255,255,0.45)', letterSpacing: '0.4px' },
  playerBadge: { display: 'flex', alignItems: 'center', gap: '12px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '14px', padding: '10px 14px' },
  playerName: { color: '#fff', fontSize: '13px', fontWeight: '700' },
  playerTier: { fontSize: '11.5px', fontWeight: '600', marginTop: '2px' },
  changeBtn: { background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(246,242,230,0.75)', fontSize: '11px', fontWeight: '700', borderRadius: '10px', padding: '8px 13px', cursor: 'pointer', fontFamily: 'inherit' },
  errorMsg: { maxWidth: '900px', margin: '0 auto 16px', background: 'rgba(211,47,47,0.2)', border: '1px solid rgba(211,47,47,0.4)', color: '#ff8a80', padding: '10px 14px', borderRadius: '10px', fontSize: '13px' },
  actionsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px', maxWidth: '900px', margin: '0 auto 20px' },
  timeControlLabel: { color: 'rgba(255,255,255,0.55)', fontSize: '11.5px', fontWeight: '700', letterSpacing: '0.4px', textTransform: 'uppercase', maxWidth: '900px', margin: '0 auto 8px' },
  timeControlRow: { display: 'flex', gap: '8px', flexWrap: 'wrap', maxWidth: '900px', margin: '0 auto 18px' },
  timeControlBtn: { background: 'rgba(255,255,255,0.06)', border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '12px', padding: '9px 14px', cursor: 'pointer', textAlign: 'center', minWidth: '68px' },
  timeControlBtnActive: { background: 'rgba(16,185,129,0.2)', borderColor: '#10B981' },
  timeControlMinutes: { color: '#fff', fontSize: '13.5px', fontWeight: '700' },
  timeControlTag: { color: 'rgba(255,255,255,0.4)', fontSize: '10px', marginTop: '2px' },
  premiumRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', maxWidth: '900px', margin: '0 auto 18px' },
  premiumCard: { background: 'linear-gradient(135deg, rgba(16,185,129,0.12), rgba(255,167,38,0.06))', border: '1px solid rgba(255,167,38,0.3)', borderRadius: '14px', padding: '14px', textAlign: 'center', cursor: 'pointer' },
  premiumIcon: { fontSize: '22px', marginBottom: '4px' },
  premiumTitle: { color: '#fff', fontSize: '12.5px', fontWeight: '700' },
  premiumSub: { color: 'rgba(255,255,255,0.45)', fontSize: '10.5px', marginTop: '2px' },  actionCard: { background: 'rgba(255,255,255,0.07)', border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '16px', padding: '20px', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit' },
  actionIcon: { fontSize: '26px', marginBottom: '8px' },
  actionTitle: { color: '#fff', fontSize: '14px', fontWeight: '700', marginBottom: '3px' },
  actionSub: { color: 'rgba(255,255,255,0.5)', fontSize: '11.5px' },
  joinBox: { display: 'flex', gap: '10px', maxWidth: '900px', margin: '0 auto 20px' },
  sectionLabel: { color: 'rgba(255,255,255,0.65)', fontSize: '12px', fontWeight: '700', letterSpacing: '0.5px', textTransform: 'uppercase', maxWidth: '900px', margin: '24px auto 10px' },
  heroWrap: { position: 'relative', overflow: 'hidden', maxWidth: '900px', margin: '4px auto 16px', borderRadius: '26px', background: 'linear-gradient(150deg, rgba(46,158,107,0.22), rgba(20,69,47,0.35) 55%, rgba(8,25,18,0.5))', border: '1px solid rgba(52,211,153,0.22)', padding: '30px 22px 26px', textAlign: 'center' },
  heroGlow: { position: 'absolute', top: '-55%', left: '50%', transform: 'translateX(-50%)', width: '420px', height: '420px', background: 'radial-gradient(circle, rgba(52,211,153,0.28), transparent 68%)', pointerEvents: 'none' },
  heroInner: { position: 'relative' },
  heroEyebrow: { color: '#34D399', fontSize: '10.5px', fontWeight: '800', letterSpacing: '2.4px' },
  heroTitle: { fontFamily: "'Fraunces', Georgia, serif", fontSize: 'clamp(26px, 6.5vw, 38px)', color: '#F6F2E6', fontWeight: '700', marginTop: '6px', lineHeight: 1.1 },
  heroSub: { color: 'rgba(246,242,230,0.62)', fontSize: '13px', marginTop: '8px' },
  playBtn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginTop: '20px', width: '100%', maxWidth: '340px', background: 'linear-gradient(135deg, #6EE7A8, #22C55E 55%, #0E9F6E)', color: '#06281A', border: 'none', padding: '19px 28px', borderRadius: '18px', fontSize: '17px', fontWeight: '900', letterSpacing: '1.2px', cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 14px 34px rgba(52,211,153,0.4), inset 0 -3px 0 rgba(0,0,0,0.16)' },
  playBtnBusy: { opacity: 0.7, cursor: 'wait' },
  playBtnIcon: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(255,255,255,0.45)' },
  heroMeta: { color: 'rgba(246,242,230,0.5)', fontSize: '11.5px', marginTop: '12px', fontWeight: '700' },
  quickRow: { display: 'flex', gap: '10px', maxWidth: '900px', margin: '0 auto 22px' },
  quickCard: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', padding: '15px 6px', cursor: 'pointer', fontFamily: 'inherit' },
  quickIcon: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', borderRadius: '13px' },
  quickLabel: { color: 'rgba(246,242,230,0.85)', fontSize: '11.5px', fontWeight: '800' },
  sheetOverlay: { position: 'fixed', inset: 0, zIndex: 950, background: 'rgba(6,16,11,0.68)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
  sheet: { width: '100%', maxWidth: '480px', maxHeight: '88vh', overflowY: 'auto', background: 'linear-gradient(180deg, #163A26, #0C2317)', borderTopLeftRadius: '26px', borderTopRightRadius: '26px', border: '1px solid rgba(255,255,255,0.1)', borderBottom: 'none', padding: '10px 18px calc(26px + env(safe-area-inset-bottom))', boxShadow: '0 -16px 44px rgba(0,0,0,0.55)' },
  sheetHandle: { width: '42px', height: '4px', borderRadius: '99px', background: 'rgba(255,255,255,0.25)', margin: '4px auto 14px' },
  sheetTitle: { fontFamily: "'Fraunces', Georgia, serif", fontSize: '20px', color: '#F6F2E6', fontWeight: '700', textAlign: 'center', marginBottom: '18px' },
  sheetLabel: { color: 'rgba(246,242,230,0.5)', fontSize: '10.5px', fontWeight: '800', letterSpacing: '1.2px', textTransform: 'uppercase', marginBottom: '9px' },
  timeRow: { display: 'flex', gap: '7px', flexWrap: 'wrap', marginBottom: '20px' },
  timeChip: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px', background: 'rgba(255,255,255,0.06)', border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '13px', padding: '9px 13px', cursor: 'pointer', fontFamily: 'inherit', minWidth: '66px' },
  timeChipActive: { background: 'linear-gradient(135deg, rgba(52,211,153,0.26), rgba(16,185,129,0.18))', borderColor: '#34D399' },
  colorRow: { display: 'flex', gap: '8px', marginBottom: '20px' },
  colorChip: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', background: 'rgba(255,255,255,0.06)', border: '1.5px solid rgba(255,255,255,0.14)', borderRadius: '13px', padding: '11px 6px', cursor: 'pointer', fontFamily: 'inherit', color: 'rgba(246,242,230,0.85)', fontSize: '12px', fontWeight: '700' },
  colorChipActive: { background: 'linear-gradient(135deg, rgba(52,211,153,0.26), rgba(16,185,129,0.18))', borderColor: '#34D399', color: '#F6F2E6' },
  colorGlyph: { fontSize: '22px', lineHeight: 1 },
  timeChipMin: { color: '#F6F2E6', fontSize: '13px', fontWeight: '800' },
  timeChipTag: { color: 'rgba(246,242,230,0.45)', fontSize: '9.5px', fontWeight: '700', letterSpacing: '0.4px' },
  modeList: { display: 'flex', flexDirection: 'column', gap: '9px' },
  modeCard: { display: 'flex', alignItems: 'center', gap: '13px', width: '100%', background: 'rgba(255,255,255,0.055)', border: '1px solid rgba(255,255,255,0.11)', borderRadius: '16px', padding: '14px 15px', cursor: 'pointer', fontFamily: 'inherit' },
  modeCardClub: { border: '1px solid rgba(52,211,153,0.4)', background: 'linear-gradient(150deg, rgba(52,211,153,0.08), rgba(255,255,255,0.03))' },
  bonusPill: { fontSize: '9.5px', fontWeight: '800', color: '#06281A', background: 'linear-gradient(135deg, #6EE7A8, #34D399)', padding: '2px 7px', borderRadius: '99px', marginLeft: '6px', verticalAlign: 'middle' },
  modeIcon: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '44px', height: '44px', borderRadius: '14px', flexShrink: 0 },
  modeTitle: { display: 'block', color: '#F6F2E6', fontSize: '14.5px', fontWeight: '800' },
  modeSub: { display: 'block', color: 'rgba(246,242,230,0.45)', fontSize: '11px', marginTop: '2px' },
  modeGo: { color: 'rgba(246,242,230,0.35)', fontSize: '20px', flexShrink: 0 },
  championBanner: { maxWidth: '900px', margin: '22px auto 0', display: 'flex', alignItems: 'center', gap: '14px', background: 'linear-gradient(135deg, rgba(16,185,129,0.16), rgba(74,222,128,0.08))', border: '1.5px solid rgba(74,222,128,0.35)', borderRadius: '16px', padding: '14px 18px' },
  championIcon: { width: '46px', height: '46px', borderRadius: '13px', background: 'rgba(74,222,128,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  championLabel: { color: '#4ADE80', fontSize: '10.5px', fontWeight: '800', letterSpacing: '1px', textTransform: 'uppercase' },
  championName: { color: '#fff', fontSize: '17px', fontWeight: '800', fontFamily: "'Fraunces', Georgia, serif", whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  championDelta: { color: '#7CD992', fontSize: '13px', fontWeight: '800' },
  championLink: { color: '#4ADE80', fontSize: '10.5px', fontWeight: '800', cursor: 'pointer', marginTop: '3px' },
  leaderboardPanel: { maxWidth: '900px', margin: '0 auto', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', padding: '8px', display: 'flex', flexDirection: 'column', gap: '2px' },
  leaderRow: { display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', borderRadius: '10px' },
  leaderRowMe: { background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(255,167,38,0.3)' },
  leaderRank: { width: '30px', height: '30px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '800', flexShrink: 0 },
  medalGold: { background: 'linear-gradient(135deg, #4ADE80, #10B981)', color: '#41300B' },
  medalSilver: { background: 'linear-gradient(135deg, #E0E0E0, #9E9E9E)', color: '#333' },
  medalBronze: { background: 'linear-gradient(135deg, #D7A86E, #A66A2E)', color: '#3D2409' },
  medalPlain: { background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.55)' },
  leaderInfo: { flex: 1, minWidth: 0 },
  leaderName: { color: '#fff', fontSize: '13.5px', fontWeight: '700', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  leaderTier: { fontSize: '11px', fontWeight: '600', marginTop: '1px' },
  deltaChip: { fontSize: '10.5px', fontWeight: '800', padding: '4px 9px', borderRadius: '20px', flexShrink: 0, marginRight: '2px' },
  leaderRating: { color: '#4ADE80', fontSize: '15px', fontWeight: '800', fontFamily: 'monospace' },
  empty: { color: C.textDim, fontSize: '13px', padding: '18px', textAlign: 'center', background: 'rgba(255,255,255,0.03)', border: `1px dashed ${C.line}`, borderRadius: '14px' },
  gameList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  gameRow: { display: 'flex', alignItems: 'center', background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.line}`, borderRadius: '14px', padding: '11px 14px', cursor: 'pointer', gap: '11px' },
  gamePlayer: { color: C.text, fontSize: '13.5px', fontWeight: '700', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  joinTag: { color: '#34D399', fontSize: '12px', fontWeight: '700' },
  watchTag: { color: '#EF5350', fontSize: '12px', fontWeight: '700' },
  // Setup screen
  welcomePage: { minHeight: '100vh', background: 'radial-gradient(circle at 30% 0%, #262D2A 0%, #1B201E 48%, #121614 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '28px 18px' },
  welcomeInner: { width: '100%', maxWidth: '420px', textAlign: 'center' },
  welcomeLogo: { width: '76px', height: '76px', borderRadius: '50%', overflow: 'hidden', margin: '0 auto 14px', border: '2.5px solid rgba(255,167,38,0.4)', boxShadow: '0 10px 34px rgba(0,0,0,0.4)' },
  welcomeEyebrow: { color: '#34D399', fontSize: '10.5px', fontWeight: '800', letterSpacing: '2px', marginBottom: '6px' },
  welcomeTitle: { fontFamily: "'Fraunces', Georgia, serif", fontSize: '34px', color: '#fff', fontWeight: '700', lineHeight: 1.1 },
  welcomeSub: { color: 'rgba(255,255,255,0.6)', fontSize: '13.5px', lineHeight: 1.65, margin: '10px auto 18px', maxWidth: '320px' },
  welcomeFeatures: { display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' },
  welcomeFeature: { display: 'inline-flex', alignItems: 'center', gap: '7px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '20px', padding: '8px 14px' },
  welcomeFeatureLabel: { color: 'rgba(255,255,255,0.8)', fontSize: '11.5px', fontWeight: '700' },
  welcomeFoot: { color: 'rgba(255,255,255,0.35)', fontSize: '11px', marginTop: '16px' },
  authTabs: { display: 'flex', gap: '6px', background: 'rgba(0,0,0,0.22)', borderRadius: '14px', padding: '5px', marginBottom: '18px' },
  authTab: { flex: 1, background: 'transparent', border: 'none', color: 'rgba(246,242,230,0.55)', padding: '11px 8px', borderRadius: '10px', fontSize: '13px', fontWeight: '800', cursor: 'pointer', fontFamily: 'inherit' },
  authTabActive: { background: 'linear-gradient(135deg, rgba(52,211,153,0.28), rgba(16,185,129,0.18))', color: '#6EE7A8', boxShadow: 'inset 0 0 0 1px rgba(52,211,153,0.4)' },
  authHint: { color: 'rgba(246,242,230,0.45)', fontSize: '11.5px', lineHeight: 1.65, marginTop: '14px', textAlign: 'left' },
  setupCard: { maxWidth: '420px', margin: '60px auto', background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(24px)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '24px', padding: '32px' },
  setupTitle: { color: '#fff', fontFamily: "'Fraunces', Georgia, serif", fontSize: '20px', margin: '20px 0 14px' },
  input: { width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.08)', border: '1.5px solid rgba(52,211,153,0.25)', borderRadius: '14px', padding: '15px 16px', color: '#fff', fontSize: '15px', marginBottom: '12px', outline: 'none', fontFamily: 'inherit' },
  checkboxRow: { display: 'flex', alignItems: 'center', gap: '8px', color: 'rgba(255,255,255,0.6)', fontSize: '12.5px', marginBottom: '12px' },
  primaryBtn: { width: '100%', padding: '16px', background: 'linear-gradient(135deg, #6EE7A8, #22C55E 55%, #0E9F6E)', border: 'none', borderRadius: '15px', color: '#06281A', fontSize: '15px', fontWeight: '900', letterSpacing: '0.6px', cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 12px 28px rgba(52,211,153,0.34), inset 0 -3px 0 rgba(0,0,0,0.15)' },
  primaryBtnSm: { padding: '0 22px', background: 'linear-gradient(135deg, #6EE7A8, #22C55E)', border: 'none', borderRadius: '12px', color: '#06281A', fontSize: '13.5px', fontWeight: '900', cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 8px 20px rgba(52,211,153,0.3)' },
};
